import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem
} from '@mui/material';
import RoleBasedAccess from '../services/RoleBasedAccess';
import { useThemeContext } from '../context/ThemeContext';
import SpiderLogo from './SpiderLogo';
import NavbarActions from './NavbarActions';
import { useLocation } from 'react-router-dom';
import { useSession } from '../context/SessionContext';

const LoggedInNavbar: React.FC = () => {
  const theme = useTheme();
  const { originalUser, handleNavigation } = useSession();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));


  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavMenuItemClick = (path: string) => {
    handleNavigation(path);
    handleMenuClose();
  };

  return (
    <AppBar position="sticky" sx={{ top: originalUser ? '40px' : 0 }}>
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box display="flex" alignItems="center">
          <Box
            display="flex"
            alignItems="center"
            // 2. The onClick behavior changes based on screen size
            onClick={isMobile ? handleMenuOpen : () => handleNavigation('/')}
            sx={{ cursor: 'pointer' }} // Always show a pointer to indicate it's clickable
          >
            <SpiderLogo />
            <Typography variant="h6" color="text.primary" mx={3}>BWI</Typography>
          </Box>
          {isMobile ? (
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
              >
                <RoleBasedAccess allowedRoles={["Internal", "Client"]}>
                  <MenuItem onClick={() => handleNavMenuItemClick('/vehicles')}>Vehicles</MenuItem>
                </RoleBasedAccess>
                <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                  <MenuItem onClick={() => handleNavMenuItemClick('/systems')}>Systems</MenuItem>
                </RoleBasedAccess>
              </Menu>
          ) : (
            <>
              <RoleBasedAccess allowedRoles={["Internal", "Client"]}>
                <Button
                  onClick={() => handleNavigation('/vehicles')}
                  sx={{
                    color: `${theme.palette.text.primary}`,
                    fontSize: '14px',
                    width: '100px',
                    minHeight: '60px !important',
                    marginX: '5px',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}>
                  Vehicles
                </Button>
              </RoleBasedAccess>
              <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                <Button
                  onClick={() => handleNavigation('/systems')}
                  sx={{
                    color: `${theme.palette.text.primary}`,
                    fontSize: '14px',
                    width: '100px',
                    minHeight: '60px !important',
                    marginX: '5px',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  Systems
                </Button>
              </RoleBasedAccess>
            </>
          )}
        </Box>
        <NavbarActions />
      </Toolbar>
    </AppBar>
  );
};

const LoggedOutNavbar: React.FC = () => {
  const { toggleDarkMode } = useThemeContext();
  return (
    <AppBar position="sticky" /* ...sx styles */>
      <Toolbar sx={{ justifyContent: 'center' }}>
        <SpiderLogo onClick={toggleDarkMode} />
      </Toolbar>
    </AppBar>
  );
};

const MasqueradeBanner = () => {
  const { user, originalUser, stopMasquerade } = useSession();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!originalUser) {
    return null;
  }

  return (
    <AppBar
      position="sticky"
      sx={{
        height: `40px`,
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 1,
        backgroundColor: '#d32f2f',
        color: '#fff',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between', 
        padding: theme.spacing(1, 2),
      }}
    >
      <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }}>
        {isMobile
          ? `Viewing as: ${user?.email}`
          : `You are currently acting as **${user?.email}** (${user?.role}).`
        }
      </Typography>
      
      {/* The button, also with shorter text for mobile */}
      <Button
        onClick={stopMasquerade}
        variant="contained"
        size="small"
        sx={{ 
          backgroundColor: '#fff', 
          color: '#d32f2f', 
          '&:hover': { backgroundColor: '#f1f1f1' },
          whiteSpace: 'nowrap',
          marginLeft: 2, 
        }}
      >
        {isMobile ? 'Exit' : `Return to Your Account`}
      </Button>
    </AppBar>
  );
};

const Navbar: React.FC = () => {
  const { isLoggedIn, isLoading } = useSession();
  const location = useLocation();
  const isLogoutPage = location.pathname.includes('/logout');

  if (!isLoggedIn || isLogoutPage) {
    return <LoggedOutNavbar />;
  }

  return (
    <>
      <MasqueradeBanner />
      <LoggedInNavbar />
    </>
  );
};

export default Navbar;
